<template>
  <Layout
    max-width="100%"
    is-card
    v-bind="$attrs"
  >
    <template #message>
      <slot name="message" />
    </template>

    <template #helper>
      <ProposalFilters
        :product-id="productId"
        @message="setMessage"
        @paginate="paginate"
      />
    </template>

    <div class="proposal-view">
      <ProposalFiltersBy
        class="mb-3"
        @paginate="paginate"
      >
        <template #actions>
          <section class="is-flex is-align-items-center">
            <button
              v-if="selectRowOptions.selected.length"
              class="button is-warning"
              @click.prevent="open({ modal: 'massStatusAlteration' })"
            >
              <span>
                Alterar {{ selectRowOptions.selected.length }} status
              </span>
            </button>
            <button
              class="button is-success ml-2"
              @click="downloadCSV"
            >
              <ExportIcon :class="!isMobile ? 'mr-2' : ''" />
              {{ !isMobile ? 'Exportar CSV' : '' }}
            </button>
          </section>
        </template>
      </ProposalFiltersBy>

      <BaseTable
        has-dbl-click
        :selectable="canDoMassAlteration"
        selectable_by="proposal_id"
        :headers="[
          { label: 'Cliente', name: 'client', show: !isMobile },
          { label: 'CPF', name: 'document', show: isMobile },
          { label: 'Nome', name: 'name', show: isMobile },
          {
            label: 'Status',
            name: 'status',
            align: 'center',
          },
          {
            label: isTypist ? 'Promotora' : 'Responsável',
            name: 'responsible',
            show: !isCreditor,
          },
          { label: 'Criado em', name: 'created_at' },
          { label: 'Valor liberado', name: 'net_amount' },
          {
            label: 'Banco',
            name: 'supplier_name',
            show: isProductFGTS && isInternalUser,
          },
          { label: 'CCB', name: 'CCB' },
          { label: 'Desembolsado Em', name: 'loaned_at' },
        ]"
        :actions="[
          {
            label: 'Visualizar simulação',
            show: !isCreditor,
            icon: () => import('@/assets/icons/ProposalDetail.vue'), // fas fa-search
            iconClass: 'not-stroke',
            action: (proposal) => open({ modal: 'simulation', data: proposal }),
          },
          {
            label: 'Ver histórico',
            show: !isCreditor,
            icon: () => import('@/assets/icons/ProposalHistory.vue'),
            iconClass: 'not-stroke',
            action: (proposal) => open({ modal: 'history', data: proposal }),
          },
          {
            label: 'Comprovação por foto',
            show: (proposal) =>
              proposal.token && (proposal.status !== 'Paga' || isInternalUser),
            icon: () => import('@/assets/icons/ProposalAntifraud.vue'),
            iconClass: 'not-stroke',
            action: (proposal) => open({ modal: 'antifraud', data: proposal }),
          },
          {
            label: 'Editar dados do usuário',
            icon: () => import('@/assets/icons/ProposalEdit.vue'),
            iconClass: 'not-stroke',
            action: (proposal) => open({ modal: 'edit', data: proposal }),
          },
          {
            label: 'Atualizar proposta',
            show: (proposal) => isProductFGTS
              && canUpdateProposalStatus(proposal.status)
              && isTodayPastCreated(proposal.created_at),
            icon: () => import('@/assets/icons/Sync.vue'),
            iconClass: 'not-stroke',
            variant: 'success',
            action: (proposal) => open({ modal: 'resimulate', data: proposal }),
          },
        ]"
        :data="proposal.list"
        @selection="(rows) => selectRowOptions.selected = rows"
        @dblClickAct="detailsProposals"
      >
        <!-- Client -->
        <template #client="{ row }">
          <CopyToClipboard class="is-highlighted">
            <span class="is-nowrap is-bold">
              {{ row.document }}
            </span>
          </CopyToClipboard>
          <p>
            {{ row?.full_name || row?.name }}
          </p>
        </template>

        <!-- Status -->
        <template #status="{ row: proposal }">
          <span
            v-tooltip="proposal.obs ?? null"
            class="tag"
            :has-action="!!proposal.issue_type
              && hasValidStatus(proposal.status)
              && !isProductCredCard
            "
            :class="proposalStatusCypher[proposal?.status]?.tag"
            @click="!!proposal.issue_type
              && !isProductCredCard
              && hasValidStatus(proposal.status)
              && open({ modal: 'issue', data: proposal })
            "
          >
            {{ proposalStatusCypher[proposal?.status]?.translation || proposal.status }}
          </span>
        </template>

        <!-- Created_at -->
        <template #created_at="{ value }">
          <span class="is-nowrap">{{ omitYear(value) }}</span>
        </template>

        <!-- Responsible -->
        <template #responsible="{ row }">
          <span
            v-tooltip="getSubAndResponsible(row)"
            class="ellipsis"
          >
            {{ getResponsibleOrPromoterData(row) }}
          </span>
        </template>

        <!-- Net Amount -->
        <template #net_amount="{ value }">
          {{ (value || 0) | money }}
        </template>

        <!-- CCB -->
        <template #CCB="{ row: proposal }">
          <template v-for="(id, idIndex) in split(proposal.external_proposal_number, ',')">
            <template v-if="id">
              <button
                v-if="hasCCBCreated(proposal)"
                :key="`btn-${idIndex}`"
                class="button is-link px-0"
                @click="open({ modal: 'status', data: proposal })"
              >
                {{ id }}
              </button>
              <span
                v-else
                :key="`span-${idIndex}`"
                class="is-ghost px-0 is-nowrap"
              >
                {{ id }}
              </span>
            </template>
          </template>
        </template>

        <!-- Loaned at -->
        <template #loaned_at="{ value }">
          {{ value || '-' }}
        </template>
      </BaseTable>

      <div class="proposal-view__footer">
        <ThePagination
          :data="pagination.data"
          @paginate="paginate"
        />
      </div>
    </div>

    <!-- check history -->
    <ModalHistory
      v-if="modal.history"
      @close="close({ key: 'history' })"
    />

    <!-- double click modal -->
    <!-- register status and start proposal -->
    <ModalProposals
      v-if="modal.proposal"
      :statuses="statuses"
      @close="close({ key: 'proposal', paginate: true })"
    />

    <!-- Proposal status -->
    <ModalProposalStatus
      v-if="modal.status && isProductFGTS && isSupplierQiTech"
      @close="close({ key: 'status' })"
      @receipt="($event) => {
        receiptData = $event;
        close({ key: 'status' });
        open({ modal: 'receipt' });
      }"
      @proposalWithdrawal="($event) => {
        withdrawalDetails = $event
        close({ key: 'status' });
        open({ modal: 'withdrawal' });
      }"
      @proposalAcquittance="($event) => {
        proposal.external_proposal_number = $event.external_proposal_number;
        close({ key: 'status' });
        open({ modal: 'acquittance' });
      }"
      @creditCardTransactionData="() => {
        close({ key: 'status' });
        open({ modal: 'creditCardTransactionData' });
      }"
    />
    <ModalProposalStatusBMP
      v-if="modal.status && isProductFGTS && isSupplierBMP"
      @close="close({ key: 'status' })"
      @receipt="($event) => {
        receiptData = $event;
        close({ key: 'status' });
        open({ modal: 'receipt' });
      }"
      @proposalWithdrawal="($event) => {
        withdrawalDetails = $event
        close({ key: 'status' });
        open({ modal: 'withdrawal' });
      }"
      @proposalAcquittance="($event) => {
        proposal.external_proposal_number = $event.external_proposal_number;
        close({ key: 'status' });
        open({ modal: 'acquittance' });
      }"
    />
    <ModalProposalStatusBMS
      v-if="modal.status && isProductFGTS && isSupplierBMS"
      @close="close({ key: 'status' })"
      @receipt="($event) => {
        receiptData = $event;
        close({ key: 'status' });
        open({ modal: 'receipt' });
      }"
      @proposalWithdrawal="($event) => {
        withdrawalDetails = $event
        close({ key: 'status' });
        open({ modal: 'withdrawal' });
      }"
      @proposalAcquittance="($event) => {
        proposal.external_proposal_number = $event.external_proposal_number;
        close({ key: 'status' });
        open({ modal: 'acquittance' });
      }"
    />
    <ModalProposalStatusCelcoin
      v-if="modal.status && isProductFGTS && isSupplierCelcoin"
      @close="close({ key: 'status' })"
      @receipt="($event) => {
        receiptData = $event;
        close({ key: 'status' });
        open({ modal: 'receipt' });
      }"
      @proposalWithdrawal="($event) => {
        withdrawalDetails = $event
        close({ key: 'status' });
        open({ modal: 'withdrawal' });
      }"
      @proposalAcquittance="($event) => {
        proposal.external_proposal_number = $event.external_proposal_number;
        close({ key: 'status' });
        open({ modal: 'acquittance' });
      }"
    />
    <ModalProposalStatusCartos
      v-if="modal.status && isProductFGTS && isSupplierCartos"
      @close="close({ key: 'status' })"
      @receipt="($event) => {
        receiptData = $event;
        close({ key: 'status' });
        open({ modal: 'receipt' });
      }"
      @proposalWithdrawal="($event) => {
        withdrawalDetails = $event
        close({ key: 'status' });
        open({ modal: 'withdrawal' });
      }"
      @proposalAcquittance="($event) => {
        proposal.proposal_id = $event.proposal_id;
        open({ modal: 'acquittance' });
        close({ key: 'status' });
      }"
    />

    <!-- open from status -->
    <ModalProposalStatusReceipt
      v-if="modal.receipt && isSupplierQiTech"
      :receipt-data="receiptData"
      @close="close({ key: 'receipt' })"
      @back="() => {
        close({ key: 'receipt' });
        open({ modal: 'status' });
      }"
    />
    <ModalProposalStatusReceiptBMP
      v-if="modal.receipt && isSupplierBMP"
      :receipt-data="receiptData"
      :details="proposal.details"
      @close="close({ key: 'receipt' })"
      @back="() => {
        close({ key: 'receipt' });
        open({ modal: 'status' });
      }"
    />
    <ModalProposalStatusReceiptBMS
      v-if="modal.receipt && isSupplierBMS"
      :receipt-data="receiptData"
      :details="proposal.details"
      @close="close({ key: 'receipt' })"
      @back="() => {
        close({ key: 'receipt' });
        open({ modal: 'status' });
      }"
    />
    <ModalProposalWithdrawal
      v-if="modal.withdrawal"
      :supplier-id="stateProposal.supplier_id"
      :details="withdrawalDetails"
      @close="close({ key: 'withdrawal' })"
      @back="() => {
        close({ key: 'withdrawal' });
        open({ modal: 'status' });
      }"
    />
    <ModalProposalAcquittance
      v-if="modal.acquittance"
      :external-proposal-number="proposal.external_proposal_number"
      @back="() => {
        close({ key: 'acquittance' });
        open({ modal: 'status' });
      }"
    />

    <!-- check credit card status -->
    <ModalProposalCreditCard
      v-if="modal.status && isProductCredCard"
      @close="close({ key: 'status' })"
      @creditCardTransactionData="() => {
        close({ key: 'status' });
        open({ modal: 'creditCardTransactionData' });
      }"
    />
    <ModalCreditCardTransactionData
      v-if="modal.creditCardTransactionData"
      @close="close({ key: 'creditCardTransactionData' })"
      @back="() => {
        close({ key: 'creditCardTransactionData' });
        open({ modal: 'status' });
      }"
    />

    <!-- edit all the given user info -->
    <ModalData
      v-if="modal.edit"
      :product-id="productId"
      :steps-list="steps"
      @close="close({ key: 'edit' })"
    />

    <!-- antifraud -->
    <AntifraudModal
      v-if="modal.antifraud"
      @close="close({ key: 'antifraud', paginate: true })"
    />

    <!-- issues -->
    <ModalIssue
      v-if="modal.issue"
      :product-id="productId"
      @close="close({ key: 'issue' })"
    />

    <!-- simulation (validation) -->
    <ModalSimulationResponse
      v-if="modal.simulation"
      :product-id="productId"
      @close="close({ key: 'simulation' })"
    />

    <!-- mass status change -->
    <BaseSimpleModal
      v-if="modal.massStatusAlteration"
      has-close-button
      width="600px"
      @close="close({ key: 'massStatusAlteration' })"
    >
      <template #title>
        Alterar status em massa
      </template>
      <template #body>
        <base-select
          v-model="form.status"
          selected=""
          :options="statuses"
          placeholder="Selecione o status"
        >
          <span>Qual status você deseja atribuir?</span>
        </base-select>
      </template>
      <template #actions>
        <div class="buttons ml-atuo">
          <button
            class="button"
            @click="close({ key: 'massStatusAlteration' })"
          >
            Cancelar
          </button>
          <button
            class="button is-primary"
            @click="confirmMassAlteration"
          >
            Alterar Status
          </button>
        </div>
      </template>
    </BaseSimpleModal>

    <!-- fgts re-simulation confirm -->
    <BaseSimpleModal
      v-if="modal.resimulate"
      has-close-button
      width="600px"
      @close="close({ key: 'resimulate' })"
    >
      <template #title>
        Simular Proposta FGTS
      </template>
      <template #body>
        <p>
          Essa ação irá atualizar a proposta em questão.
          É necessário fazer isso, no caso da proposta
          <strong>não ter sido paga no dia de digitação da mesma</strong>.
        </p>
        <p class="mt-3">
          Deseja prosseguir?
        </p>
      </template>
      <template #actions>
        <div class="buttons ml-auto">
          <button
            class="button"
            @click="close({ key: 'resimulate' })"
          >
            Cancelar
          </button>
          <button
            class="button is-primary"
            @click="reSimulateProposalFGTS"
          >
            Simular novamente
          </button>
        </div>
      </template>
    </BaseSimpleModal>

    <!-- others modals -->
    <slot />
  </Layout>
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex';
import { toast } from 'bulma-toast';

import {
  accounts as accountsAPI,
  proposals as proposalsAPI,
  qitech as qitechAPI,
} from '@/services';

import config from '@/data/config';
import filterStorageMixin from '@/mixins/filterStorage.mixin';
import loadingMixin from '@/mixins/loadingView.mixin';
import roleManagerMixin from '@/mixins/roleManager.mixin';
import sanitizeHtmlMixin from '@/mixins/sanitizeHtml.mixin';

import Layout from '@/layouts/Layout.vue';
import ThePagination from '@/components/ThePagination.vue';
import ProposalFilters from '@/components/ProposalFilters.vue';
import ProposalFiltersBy from '@/components/ProposalFiltersBy.vue';

import { cypher as proposalStatusCypher } from '@/data/proposalStatusCypher.js';

export default {
  name: 'ProposalsList',
  components: {
    Layout,
    ThePagination,
    ProposalFilters,
    ProposalFiltersBy,

    // async
    ExportIcon: () => import('@/assets/icons/ExportIcon.vue'),
    CopyToClipboard: () => import('@/components/CopyToClipboard.vue'),
    BaseSelect: () => import('@/components/Base/Select/index.vue'),
    BaseSimpleModal: () => import('@/components/BaseSimpleModal.vue'),
    BaseTable: () => import('@/components/Base/Table/index.vue'),

    // modals
    ModalProposals: () => import('@/components/ModalProposals.vue'),
    ModalHistory: () => import('@/components/ModalHistory.vue'),
    ModalData: () => import('@/components/ModalData/index.vue'),
    ModalIssue: () => import('@/components/ModalIssue.vue'),
    ModalSimulationResponse: () => import('@/components/ModalSimulationResponse.vue'),
    AntifraudModal: () => import('@/components/AntifraudModal/Index.vue'),

    // ccb
    ModalProposalStatus: () => import('@/components/ModalProposalStatus.vue'),
    ModalProposalStatusBMP: () => import('@/components/ModalProposalStatusBMP.vue'),
    ModalProposalStatusBMS: () => import('@/components/ModalProposalStatusBMS.vue'),
    ModalProposalStatusCartos: () => import('@/components/ModalProposalStatusCartos.vue'),
    ModalProposalStatusCelcoin: () => import('@/components/ModalProposalStatusCelcoin.vue'),

    // receipt
    ModalProposalStatusReceipt: () => import('@/components/ModalProposalStatusReceipt.vue'),
    ModalProposalStatusReceiptBMP: () => import('@/components/ModalProposalStatusReceiptBMP.vue'),
    ModalProposalStatusReceiptBMS: () => import('@/components/ModalProposalStatusReceiptBMS.vue'),

    // acquittance
    ModalProposalAcquittance: () => import('@/components/ModalProposalAcquittance.vue'),
    ModalProposalWithdrawal: () => import('@/components/ModalProposalWithdrawal.vue'),

    // credit card
    ModalProposalCreditCard: () => import('@/components/ModalProposalCreditCard.vue'),
    ModalCreditCardTransactionData: () => import('@/components/ModalCreditCardTransactionData.vue'),
  },
  mixins: [
    filterStorageMixin,
    loadingMixin,
    roleManagerMixin,
    sanitizeHtmlMixin,
  ],
  props: {
    productId: {
      type: [Number, String],
      required: true,
    },
    statuses: {
      type: Array,
      required: true,
    },
    steps: {
      type: Set,
      required: true,
    },
  },
  data: () => ({
    message: null,
    withdrawalDetails: null,
    receiptData: false,
    selectRowOptions: {
      selectAll: false,
      selected: [],
    },
    form: {
      status: '',
    },
    proposal: {
      list: [],
    },
    modal: {
      proposal: false,
      history: false,
      status: false,
      receipt: false,
      edit: false,
      antifraud: false,
      withdrawal: false,
      acquittance: false,
      creditCardTransactionData: false,
      issue: false,
      simulation: false,
      massStatusAlteration: false,
      resimulate: false,
    },
    pagination: {
      data: {},
    },
    proposalStatusCypher,
  }),
  computed: {
    ...mapState({
      stateProposal: (state) => state.proposal.proposal,
    }),
    ...mapGetters({
      isMobile: 'app/isMobile',
      filters: 'proposal/validFilters',
      isInternalUser: 'user/isInternalUser',
    }),
    isProductFGTS() {
      return this.productId === config.PRODUCT_ID_FGTS;
    },
    isProductCredCard() {
      return this.productId === config.PRODUCT_ID_CREDIT_CARD;
    },
    isSupplierQiTech() {
      return this.stateProposal.supplier_id === config.SUPPLIER_ID_QITECH;
    },
    isSupplierBMP() {
      return this.stateProposal.supplier_id === config.SUPPLIER_ID_BMP;
    },
    isSupplierBMS() {
      return this.stateProposal.supplier_id === config.SUPPLIER_ID_BMS;
    },
    isSupplierCartos() {
      return this.stateProposal.supplier_id === config.SUPPLIER_ID_CARTOS;
    },
    isSupplierCelcoin() {
      return this.stateProposal.supplier_id === config.SUPPLIER_ID_CELCOIN;
    },
    canDoMassAlteration() {
      return [
        this.isAntifraud,
        this.isAdmin,
        this.isSuper,
      ].some((role) => role);
    },
    canEditObs() {
      return [
        this.isAdmin,
        this.isSuper,
        this.isManager,
        this.isAntifraud,
        this.isCommercial,
        this.isCompliance,
        this.isMasterOrFinancial,
      ].some((isValid) => isValid);
    },
  },
  watch: {
    'modal.massStatusAlteration': {
      handler(status) {
        if (status === true) return;
        this.selectRowOptions.selectAll = false;
        this.selectRowOptions.selected = [];
      },
    },
    loading: {
      handler(status) {
        if (status === true) this.setMessage('');
      },
    },
  },
  mounted() {
    this.$nextTick(() => this.close({ key: 'all', paginate: true }));
  },
  beforeDestroy() {
    this.setProposalData({});
    if (!this.isInternalUser) this.resetFilters();
  },
  methods: {
    ...mapActions({
      setProposalData: 'proposal/SET_PROPOSAL_DATA',
      resetFilters: 'proposal/RESET_FILTERS',
    }),
    canUpdateProposalStatus(proposalStatus) {
      return [
        'Em análise',
        'Pendência',
        'Aguardando videoconferência',
      ].includes(proposalStatus);
    },

    // handlers
    handlerError(err) {
      let message = err?.response?.data?.error?.message
        ?? err?.response?.data?.message
        ?? err?.error?.response?.data?.error?.message
        ?? err?.error?.data?.message
        ?? err?.message
        ?? err;

      if (err?.response?.data?.error?.errors) {
        const errors = Object.values(err?.response?.data?.error?.errors);

        message = message.concat(`<br> - ${errors.map((error) => error).join('<br> - ')}`);
      }

      this.setMessage(message);
      this.pagination.data = {};
      this.proposal.list = [];
    },
    setMessage(message) {
      this.message = message;
    },
    split(arr, data) {
      return String(arr || '').replace(/\/[A-Za-z0-9]+$/, '').split(data);
    },
    getSubAndResponsible({ promoter_name, responsible_name }) {
      return `Sub: ${promoter_name} - Resp: ${responsible_name}`;
    },
    getResponsibleOrPromoterData({ promoter_name, responsible_name }) {
      return this.isTypist ? promoter_name : responsible_name;
    },
    toggleSelectRow(proposalId) {
      const { selected } = this.selectRowOptions;
      const isSelected = selected.find((row) => row === proposalId);

      if (isSelected) {
        const selectedIndex = selected.indexOf(isSelected);
        selected.splice(selectedIndex, 1);
      } else {
        selected.push(proposalId);
      }

      // resolve caveat of length reactivity for nested arrays https://v2.vuejs.org/v2/guide/reactivity
      selected.splice(selected.length);
    },
    toggleSelectAll() {
      const checkboxList = document.querySelectorAll('.row-checkbox');

      this.selectRowOptions.selectAll = !this.selectRowOptions.selectAll;
      this.selectRowOptions.selected = [];

      checkboxList.forEach((elem) => {
        elem.checked = false;
      });

      if (this.selectRowOptions.selectAll) {
        this.selectRowOptions.selected = this.proposal.list
          .map((item) => item.proposal_id);

        checkboxList.forEach((elem) => {
          elem.checked = true;
        });
      }

      // resolve caveat of length reactivity for nested arrays https://v2.vuejs.org/v2/guide/reactivity
      this.selectRowOptions.selected.splice(this.selectRowOptions.selected.length);
    },
    async downloadCSV() {
      const product_id = this.productId;

      this.loading = true;

      await proposalsAPI
        .downloadCSV({ ...this.filters, product_id, output: 'csv' })
        .then((data) => {
          const blob = new Blob([`\ufeff${data}`], { type: 'text/csv;charset=utf-8' });
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement('a');

          link.href = url;
          link.setAttribute('download', 'propostas.csv');
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        })
        .catch(this.handlerError)
        .finally(() => {
          this.loading = false;
        });
    },
    hasCCBCreated({ external_proposal_number }) {
      const status = external_proposal_number?.toLowerCase() || '';
      return status !== 'ccb não gerada';
    },
    hasValidStatus(status) {
      return [
        'Em análise',
        'Pendência',
        'Aguardando consulta',
        'Aguardando pagamento',
        'Aguardando relançamento',
        'Aguardando desaverbação',
      ].some((option) => option === status);
    },
    omitYear(created) {
      const [date, hour] = created.split(' ');
      const [day, month] = date.split('/');

      return `${day}/${month} ${hour}`;
    },
    isTodayPastCreated(date) {
      let [splittedDate] = date.split(' ');
      splittedDate = splittedDate.split('/');

      if (splittedDate.length === 2) splittedDate.push('2024');

      const [day, month, year] = splittedDate;

      const created = new Date(year, month - 1, day);
      const today = new Date();

      today.setHours(0, 0, 0, 0);
      created.setHours(0, 0, 0, 0);

      return today.getTime() > created.getTime();
    },

    // api
    async paginate({ page = 1 } = {}) {
      this.loading = true;

      await proposalsAPI
        .getProposals({ page, ...this.filters, product_id: this.productId })
        .then(({ data }) => {
          this.$set(this.pagination, 'data', data);
          this.$set(this.proposal, 'list', data.data);
        })
        .catch(this.handlerError)
        .finally(() => {
          this.loading = false;
        });
    },
    async detailsProposals({ event, ...proposal }) {
      const invalidNodes = ['button', 'input'];

      if (!this.canEditObs) return;
      if (this.isCreditor || invalidNodes.includes(event.target.localName)) return;

      if (proposal) this.setProposalData(proposal);

      this.loading = true;

      try {
        await accountsAPI.getInfoUser();
        this.modal.proposal = true;
      } catch (error) {
        this.handlerError(error);
      } finally {
        this.loading = false;
      }
    },
    async confirmMassAlteration() {
      this.loading = true;

      await proposalsAPI
        .massStatusUpdate(this.selectRowOptions.selected, this.form.status)
        .then(() => {
          toast({
            message: 'Status atualizado com sucesso!',
            type: 'is-success',
            dismissible: true,
            pauseOnHover: true,
            animate: { in: 'fadeIn', out: 'fadeOut' },
            position: 'top-center',
            closeOnClick: true,
          });
        })
        .catch(this.handlerError)
        .finally(() => {
          this.loading = false;
          this.close({
            key: 'massStatusAlteration',
            paginate: true,
          });
        });
    },
    async reSimulateProposalFGTS() {
      this.loading = true;

      await qitechAPI
        .reSimulateFGTS({ proposal_id: this.stateProposal.proposal_id })
        .then(({ data }) => {
          toast({
            message: data.data,
            type: 'is-success',
            dismissible: true,
            pauseOnHover: true,
            animate: { in: 'fadeIn', out: 'fadeOut' },
            position: 'top-center',
            closeOnClick: true,
          });
        })
        .catch(this.handlerError)
        .finally(() => {
          this.loading = false;
          this.close({
            key: 'resimulateFGTS',
            paginate: true,
          });
        });
    },

    // modals
    open({ modal, data = null }) {
      if (data !== null) this.setProposalData(data);
      this.modal[modal] = true;
    },
    close({ key, paginate = false }) {
      if (key === 'all') {
        for (const label of Object.keys(this.modal)) {
          this.modal[label] = false;
        }
      } else {
        this.modal[key] = false;
      }

      if (paginate) this.paginate();
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@/assets/scss/src/_textVariables.scss';
@import '@/assets/scss/src/_buttonsVariables.scss';

.selected-operation-list {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
}

.slide-leave-to {
  transform: translateX(-20px);
  opacity: 0;
}

.slide-leave-active {
  transition: all 0.2s ease-out;
}
.slide-enter-active {
  transition: all 0.2s ease;
}
.slide-enter {
  transform: translateX(-20px);
  opacity: 0;
}
.showActionButton {
  &button {
    text-decoration: none;
  }

  i {
    font-size: 1.5rem;
  }
  &.open {
    transform: rotate(-180deg);
    transition: transform .5s;
  }
  &.close {
    transform: rotate(0deg);
    transition: transform .5s;
  }
}
.proposal-view {
  display: flex;
  flex-direction: column;

  &__footer {
    display: flex;

    &--filter {
      margin-left: auto;

      display: flex;
      padding: 20px;

      align-items: center;
      justify-content: center;

      p {
        $recuo: 30px;

        margin-right: $recuo * (-1);

        display: none;
        padding: 5px #{$recuo * 1.5} 5px #{$recuo};
        border-radius: 30px 0 0 30px;
        background: $color-primary;

        font-size: 22px;
        color: $color-light;
        text-align: center;

        pointer-events: none;

        @media (min-width: 768px) {
          display: block;
        }
      }

      i {
        padding: 15px;
        border-radius: 50%;
        background: $color-secondary;
        box-shadow: 2px 2px 5px 0px rgba(#ABABAB, 0.75);

        font-size: 25px;
        color: $color-light;

        transition: all .3s ease-in-out;
        cursor: pointer;

        @media(max-width: 768px){
          font-size: 18px;
        }

        &:hover {
          background: $color-primary;
        }
      }

      &.is-filtered {
        i {
          background: $color-primary;
          color: $color-light;
        }
      }
    }
  }
}

.sticky {
  position: sticky;
  top: 0;
  z-index: 1;

  background-color: #fff;
}

.ellipsis {
  display: block;
  max-width: 200px;
  overflow: hidden;

  white-space: nowrap;
  text-overflow: ellipsis;
}
</style>
